import { Inbox01, LogOut01, Menu01, XClose, SearchLg, Settings01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { openAgentConversationPanel } from 'src/app/AgentConversationPanel/agentConversationPanel.slice';
import LogoSmall from 'src/assets/img/logo/logo-icon.svg';
import LogoWhite from 'src/assets/img/logo/Wal-full-logo-white.svg';
import InputText from 'src/components/InputText/InputText';
import Notification from 'src/components/NotificationCmp/Notification';
import NotificationBubbleStack from 'src/components/NotificationCmp/NotificationBubbleStack';
import OrbAvatar from 'src/components/OrbAvatar';
import TruncateTextCmp from 'src/components/TruncateText';
import { ROUTES } from 'src/constants/routes';
import { resetAuth } from 'src/redux/auth.slice';
import { type AppDispatch, type RootState, persistor } from 'src/redux/store';
import { getNotifications, transformNotification, NotificationItem } from 'src/services/NotificationService';
import { getShortName, onError, getOS, formatNumber } from 'src/utils/CommonFunctions';
import { hasMenuPermission } from 'src/utils/permissions';

import { resetAddQuoteData } from '../MyQuote/AddQuote/AddQuote.slice';

const NOTIFICATION_LIMIT = 6;

interface IProps {
    setIsSidebarOpen: (isSidebarOpen: boolean) => void;
    isSidebarOpen: boolean;
}

const TopHeader = ({ isSidebarOpen, setIsSidebarOpen }: IProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    // Feature gate: resolved portal visibility comes from getUserDetails API → auth.portalVisibility
    const isAgentViewVisible = useSelector((state: RootState) => state.auth.portalVisibility?.enableAgentView === true);
    // const appearance = useSelector((state: RootState) => state.basic.appearance); // header logo is static now — see the Link below

    const divRef = useRef<any>(null);
    const userNameRef = useRef<HTMLHeadingElement>(null);
    const isOpenSettingMenuRef = useRef<any>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const currentOS = useMemo(() => getOS(), []);

    const searchShortcutKey = useMemo(() => (currentOS === 'Windows' ? 'Y' : 'K'), [currentOS]);

    const [isOpenSettingMenu, setIsOpenSettingMenu] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);

    // Preloaded notification state
    const [preloadedNotifications, setPreloadedNotifications] = useState<any[]>([]);
    // Queue of socket-received notifications shown as transient bubbles (append-only)
    const [bubbleQueue, setBubbleQueue] = useState<any[]>([]);
    const [isNotificationsLoading, setIsNotificationsLoading] = useState(true);
    const [notificationsTotals, setNotificationsTotals] = useState(0);
    // Unread rows only — the badge. Totals above counts every kept row (read history included)
    // now that reads mark rather than delete, so it can no longer drive the badge.
    const [notificationsUnread, setNotificationsUnread] = useState(0);
    const [notificationsHasNextPage, setNotificationsHasNextPage] = useState(false);

    const { isSocketConnected, socketIo } = useSelector((state: RootState) => state.basic);

    const dropdownOptions = [
        {
            path: ROUTES.SETTING,
            displayName: 'Account Settings',
            Icon: Settings01,
            // WAL staff sessions have no contact profile to edit (backend rejects profile updates)
            permission: hasMenuPermission(currentUser?.role, 'Account Settings') && !currentUser?.isWalUserSession,
        },
    ];

    // Listen for live notifications via socket
    useEffect(() => {
        if (isSocketConnected && socketIo?.on && currentUser?.id) {
            const roomName = `notification_CUSTOMER_${currentUser.id}`;

            socketIo.post('/subscribe/joinRoom', { roomNames: [roomName] }, (body: any, jwr: any) => {
                console.log('Join notification room response:', roomName, body, jwr?.statusCode);
            });

            const handleNewNotification = (data: any) => {
                console.log('New notification received:', data);

                if (data) {
                    const transformed = transformNotification(data);

                    setPreloadedNotifications((prev) => {
                        if (prev.some((n) => n.id === transformed.id)) {
                            return prev;
                        }

                        return [transformed, ...prev];
                    });
                    setNotificationsTotals((prev) => prev + 1);
                    setNotificationsUnread((prev) => prev + 1);
                    // Push to the bubble queue so the stack shows a transient pop-up
                    setBubbleQueue((prev) => [...prev, transformed]);
                }
            };

            socketIo.on('notification', handleNewNotification);

            return () => {
                socketIo.off('notification', handleNewNotification);
            };
        }
    }, [isSocketConnected, socketIo, currentUser?.id]);

    // Preload notifications on mount
    const fetchInitialNotifications = useCallback(async () => {
        try {
            setIsNotificationsLoading(true);

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            abortControllerRef.current = new AbortController();

            const response = await getNotifications(
                {
                    page: 1,
                    limit: NOTIFICATION_LIMIT,
                },
                abortControllerRef.current.signal
            );

            if (response?.data?.notifications) {
                const transformedNotifications = response.data.notifications.map((notification: NotificationItem) => transformNotification(notification));

                setPreloadedNotifications(transformedNotifications);
                setNotificationsTotals(response.data.pagination?.totalItems || 0);
                setNotificationsUnread(response.data.unreadTotal ?? (response.data.pagination?.totalItems || 0));
                setNotificationsHasNextPage(response.data.pagination?.hasNextPage || false);
            }
        } catch (error: any) {
            if (error.code !== 'ERR_CANCELED') {
                console.error('Error fetching notifications:', error);
            }
        } finally {
            setIsNotificationsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchInitialNotifications();
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [isAuthenticated, fetchInitialNotifications]);

    const handleLogout = () => {
        persistor.pause(); // stop redux-persist from re-writing state after reset
        persistor.purge();
        localStorage.clear();
        dispatch(resetAuth() as any);
        dispatch(resetAddQuoteData({ clearAll: true }));
    };

    const handleClickOutside = (event: any) => {
        if (divRef.current && !divRef.current.contains(event.target) && isOpenSettingMenuRef.current && !isOpenSettingMenuRef.current.contains(event.target)) {
            setIsOpenSettingMenu(false);
        }
    };

    useEffect(() => {
        if (isOpenSettingMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpenSettingMenu]);

    const handleNotificationUpdate = (updatedNotifications: any[], totals: number, hasNextPage: boolean) => {
        setPreloadedNotifications(updatedNotifications);
        setNotificationsTotals(totals);
        setNotificationsHasNextPage(hasNextPage);
    };

    // When the notification panel closes with an empty list but more pages exist on the server,
    // re-fetch so the badge and next panel open are populated correctly.
    // This handles the case where the Notification component unmounts mid-fetch (e.g. after navigation)
    // and the in-flight request gets aborted before the next page can be loaded.
    useEffect(() => {
        if (!isNotificationOpen && preloadedNotifications.length === 0 && notificationsHasNextPage) {
            fetchInitialNotifications();
        }
    }, [isNotificationOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <div className="flex items-center justify-between gap-x-2 py-[9px] mdm:px-5 px-3">
                <div className="flex-1">
                    <Link to={ROUTES.HOME} className="flex">
                        {/* Always the static WAL logo — the org-uploaded appearance logo is intentionally
                            NOT used here (it still brands carriers/documents elsewhere in the portal) */}
                        <img src={LogoWhite} alt="logo-light" className="h-[28px] object-contain md:inline-block hidden min-w-[187px]" />

                        <img src={LogoSmall} alt="logo-light" className="h-[28px] max-w-[40px] object-contain md:hidden z-[7] relative" />
                    </Link>
                </div>

                <div className="flex gap-x-2 w-[calc(100%-340px)] max-sm:w-full justify-center">
                    <InputText
                        parentClassName="flex-1 max-w-[500px]"
                        inputName={t('Common.Search')}
                        placeholder={t('Common.Search')}
                        inputWrapClass="border-gradient-with-button"
                        className="z-[1] pl-[36px] max-sm:pl-[34px] pr-11 max-sm:pr-3 rounded-[10px] border-gray500 hover:border-borderPrimary bg-grayLight900 focus:bg-grayLight900 focus:border-borderPrimary placeholder:text-white800 text-white800 shadow-sm font-normal !border-0 !bg-transparent !py-[11px] max-sm:!py-2 !h-auto"
                        icon={
                            <>
                                <SearchLg className="absolute -translate-y-2/4 top-2/4 left-3 max-sm:left-2.5 text-borderPrimary h-4 w-4" />
                                <div className="rounded px-1 py-px bg-white bg-opacity-[0.08] text-utilityGray700 text-xs font-medium absolute -translate-y-2/4 top-2/4 right-3 max-sm:hidden">
                                    ⌘{searchShortcutKey}
                                </div>
                            </>
                        }
                        isClearable={true}
                        value={''}
                        onChangeFunc={() => {}}
                        autoFocus={false}
                    />
                </div>
                <div className="flex items-center gap-x-3 max-sm:gap-x-2 flex-1 justify-end">
                    {/* Always visible — hiding the whole button at zero made the empty state
                        unreachable. Only the badge is gated, and it counts unread rows. */}
                    <div className="rounded-[10px] p-2 flex items-center cursor-pointer border-gradient-with-select relative" onClick={() => setIsNotificationOpen(true)}>
                        <Inbox01 className="w-4 h-4 text-borderPrimary" />
                        {notificationsUnread > 0 && (
                            <span className="absolute -top-[5px] -right-[5px] min-w-[14px] h-[14px] bg-fgErrorPrimary rounded-full flex items-center justify-center px-1">
                                <span className="text-white text-[9px] leading-[14px] font-medium">{formatNumber(notificationsUnread, false)}</span>
                            </span>
                        )}
                    </div>
                    {isAgentViewVisible && (
                        <div
                            className="relative rounded-[10px] w-8 h-8 flex items-center justify-center cursor-pointer overflow-hidden flex-none"
                            onClick={() => dispatch(openAgentConversationPanel())}
                        >
                            <OrbAvatar size={32} />
                        </div>
                    )}
                    <div className="relative">
                        <div
                            className={`rounded-[10px] flex items-center gap-x-1 cursor-pointer border-gradient-with-select max-md:p-0 max-md:[&:after]:hidden ${
                                isOpenSettingMenu ? 'button-clicked' : ''
                            }`}
                            onClick={() => {
                                setIsOpenSettingMenu(!isOpenSettingMenu);
                            }}
                            ref={isOpenSettingMenuRef}
                        >
                            {currentUser?.image ? (
                                <img
                                    src={`${currentUser.imageUrl}${currentUser.image}`}
                                    className="w-8 h-8 max-md:w-8 max-md:h-8 rounded-[10px] border border-utilityBlack border-opacity-[0.08] flex-none object-contain"
                                    alt={isAuthenticated ? `${currentUser.firstName} ${currentUser.lastName}` : ''}
                                    title={isAuthenticated ? `${currentUser.firstName} ${currentUser.lastName}` : ''}
                                    onError={onError}
                                />
                            ) : (
                                <div className="w-8 h-8 max-md:w-8 max-md:h-8 rounded-[10px] border border-utilityBlack border-opacity-[0.08] flex-none flex items-center justify-center uppercase bg-primary text-white text-xs">
                                    {getShortName(`${currentUser?.firstName} ${currentUser?.lastName}`)}
                                </div>
                            )}
                        </div>
                        <div
                            className={`rounded-xl border border-utilityGray200 bg-white shadow-lg right-0 w-[295px] top-full z-[999] absolute overflow-hidden mt-5 ${
                                isOpenSettingMenu ? '' : 'hidden'
                            }`}
                            ref={divRef}
                        >
                            <div className="flex items-center gap-x-2.5 p-4 border-b border-utilityGray200 ">
                                {currentUser?.image ? (
                                    <img
                                        src={`${currentUser.imageUrl}${currentUser.image}`}
                                        className="w-8 h-8 object-cover rounded-md border-[0.75px] border-black/[0.08] flex-none text-[8px] overflow-hidden"
                                        alt={isAuthenticated ? `${currentUser.firstName} ${currentUser.lastName}` : ''}
                                        onError={onError}
                                    />
                                ) : (
                                    <div className="w-8 h-8 text-xs rounded-md bg-primary text-white flex items-center justify-center uppercase border-gray-200 border-[1px] flex-none">
                                        {getShortName(`${currentUser?.firstName} ${currentUser?.lastName}`)}
                                    </div>
                                )}

                                <div className=" flex-grow overflow-hidden min-w-0">
                                    <TruncateTextCmp message={`${currentUser?.firstName} ${currentUser?.lastName}`} refName={userNameRef}>
                                        <h4 ref={userNameRef} className="text-textSecondary text-xs font-semibold truncate block">
                                            {isAuthenticated && `${currentUser?.firstName} ${currentUser?.lastName}`}
                                        </h4>
                                    </TruncateTextCmp>

                                    <div className="text-grayLight600 text-[10px] leading-[14px] font-normal truncate">{currentUser?.email}</div>
                                </div>
                            </div>
                            <div className="px-3 py-2 ">
                                {dropdownOptions &&
                                    dropdownOptions.map((option: any) => {
                                        const { path, displayName, permission, Icon } = option;

                                        return (
                                            <React.Fragment key={`dropdown_nav_${path}`}>
                                                <NavLink
                                                    key={`dropdown_nav_${path}`}
                                                    to={path}
                                                    className={`px-2 py-1.5 cursor-pointer hover:bg-gray50 [&.active]:bg-gray50 flex items-center gap-x-1.5 rounded-md ${
                                                        permission ? 'visible' : 'hidden'
                                                    }`}
                                                    onClick={() => {
                                                        setIsOpenSettingMenu(false);
                                                    }}
                                                >
                                                    <div>
                                                        <Icon className="w-4 h-4 text-grayLight600" />
                                                    </div>
                                                    <p className="text-textSecondary text-xs font-medium">{displayName}</p>
                                                </NavLink>
                                            </React.Fragment>
                                        );
                                    })}
                                <div className="px-2 py-1.5 cursor-pointer hover:bg-gray50 flex items-center gap-x-1.5 rounded-md" onClick={handleLogout}>
                                    <LogOut01 className="w-4 h-4 text-grayLight600" />
                                    <p className="text-textSecondary text-xs leading-[1.7] font-medium">Sign out</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="rounded-[10px] p-2 items-center cursor-pointer border-gradient-with-select hidden max-md:flex"
                        data-sidebar-toggle="true"
                        onClick={() => {
                            setIsSidebarOpen(!isSidebarOpen);
                        }}
                    >
                        {isSidebarOpen ? <XClose className="w-4 h-4 text-borderPrimary" /> : <Menu01 className="w-4 h-4 text-borderPrimary" />}
                    </div>
                </div>
            </div>

            {isNotificationOpen && (
                <Notification
                    handleClose={() => setIsNotificationOpen(false)}
                    unreadTotal={notificationsUnread}
                    onUnreadTotalChange={setNotificationsUnread}
                    initialNotifications={preloadedNotifications}
                    initialLoading={isNotificationsLoading}
                    initialTotals={notificationsTotals}
                    initialHasNextPage={notificationsHasNextPage}
                    onNotificationUpdate={handleNotificationUpdate}
                />
            )}

            <NotificationBubbleStack notifications={bubbleQueue} />
        </>
    );
};

export default TopHeader;
